import React, { useState, useEffect } from 'react';
import { 
  Award, 
  Search,
  Calendar,
  Gift,
  Zap,
  ChevronRight,
  Filter,
  X,
  Users,
  Trophy,
  Medal
} from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { useSyncedUser } from '../../context/SyncedUserContext';
import Loader from '../ui/loader';
import useUser from '../../api/user';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "../ui/drawer";

// Filter drawer component
const FilterDrawer = ({ 
  isOpen, 
  onClose, 
  activeTime,
  setActiveTime,
  searchQuery, 
  setSearchQuery,
  timeFilters
}) => {
  const { t } = useTranslation();
  // Local state to store values while drawer is open
  const [localTimeFilter, setLocalTimeFilter] = useState(activeTime);
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);
  
  // Reset local values when drawer opens
  useEffect(() => {
    if (isOpen) {
      setLocalTimeFilter(activeTime);
      setLocalSearchQuery(searchQuery);
    }
  }, [isOpen, activeTime, searchQuery]);
  
  // Handle apply - update parent state with local values
  const handleApply = () => {
    setActiveTime(localTimeFilter);
    setSearchQuery(localSearchQuery);
    onClose();
  };
  
  // Handle reset filters
  const handleReset = () => {
    setLocalTimeFilter('all_time');
    setLocalSearchQuery('');
  };

  return (
    <Drawer open={isOpen} onOpenChange={onClose}>
      <DrawerContent className="max-h-[85vh]">
        <div className="flex flex-col h-[85vh]">
          <DrawerHeader className="border-b border-neutral-200 dark:border-neutral-700 pb-2">
            <div className="flex items-center justify-between">
              <DrawerTitle className="flex items-center gap-2 text-base">
                <Filter className="h-5 w-5 text-primary" />
                <span>{t('leaderboard.filters.title')}</span>
              </DrawerTitle>
              <DrawerClose className="rounded-full p-1.5 hover:bg-neutral-100 dark:hover:bg-neutral-800">
                <X className="h-4 w-4" />
              </DrawerClose>
            </div>
            <DrawerDescription className="mt-1">
              {t('leaderboard.filters.description')}
            </DrawerDescription>
          </DrawerHeader>

          {/* Scrollable content area */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5">
            {/* Time Period Options */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium mb-2">{t('leaderboard.filters.timePeriod')}</h4>
              <div className="grid grid-cols-2 gap-2">
                {timeFilters.map((filter) => (
                  <button
                    key={filter.id}
                    type="button"
                    onClick={() => setLocalTimeFilter(filter.id)}
                    className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg ${
                      localTimeFilter === filter.id
                        ? 'bg-primary text-white'
                        : 'bg-neutral-100 dark:bg-neutral-800'
                    }`}
                  >
                    <filter.icon className="w-4 h-4" />
                    <span className="text-sm">{filter.label}</span>
                  </button>
                ))}
              </div>
            </div>
            
            {/* Search Input */}
            <div className="space-y-2">
              <h4 className="text-sm font-medium mb-2">{t('leaderboard.filters.search')}</h4>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <input
                  type="text"
                  placeholder={t('leaderboard.filters.searchPlaceholder')}
                  className="w-full pl-10 pr-4 py-2 rounded-lg bg-neutral-100 dark:bg-neutral-800 
                           border border-transparent focus:border-primary focus:ring-2 
                           focus:ring-primary/20 transition-all duration-200"
                  value={localSearchQuery}
                  onChange={(e) => setLocalSearchQuery(e.target.value)}
                />
              </div>
            </div>
            
            {/* Reset Filters (only if filters are applied) */}
            {(localTimeFilter !== 'all_time' || localSearchQuery) && (
              <div className="pt-2">
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={handleReset}
                >
                  <X className="w-4 h-4 mr-2" />
                  {t('leaderboard.filters.reset')}
                </Button>
              </div>
            )}
          </div>

          {/* Footer with Apply button */}
          <DrawerFooter className="border-t border-neutral-200 dark:border-neutral-700 pt-3 pb-5">
            <Button
              onClick={handleApply}
              className="w-full"
            >
              {t('leaderboard.filters.apply')}
            </Button>
            <DrawerClose asChild>
              <Button variant="outline" className="w-full mt-2">
                {t('common.cancel')}
              </Button>
            </DrawerClose>
          </DrawerFooter>
        </div>
      </DrawerContent>
    </Drawer>
  );
};

// Rank badge component
const RankBadge = ({ rank }) => {
  if (rank === 1) {
    return (
      <div className="flex items-center justify-center h-8 w-8 rounded-full bg-amber-100 dark:bg-amber-900/30">
        <Trophy className="h-4 w-4 text-amber-500 fill-amber-500" />
      </div>
    );
  } else if (rank === 2) {
    return (
      <div className="flex items-center justify-center h-8 w-8 rounded-full bg-neutral-200 dark:bg-neutral-700/50">
        <Medal className="h-4 w-4 text-neutral-600 dark:text-neutral-300" />
      </div>
    );
  } else if (rank === 3) {
    return (
      <div className="flex items-center justify-center h-8 w-8 rounded-full bg-amber-50 dark:bg-amber-900/20">
        <Medal className="h-4 w-4 text-amber-700 dark:text-amber-500" />
      </div>
    );
  } else {
    return (
      <div className="flex items-center justify-center h-8 w-8 rounded-full bg-neutral-100 dark:bg-neutral-800">
        <span className="text-sm font-medium text-neutral-600 dark:text-neutral-400">{rank}</span>
      </div>
    );
  }
};

const Leaderboard = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [activeTime, setActiveTime] = useState('all_time');
  const [searchQuery, setSearchQuery] = useState('');
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const { syncedUserData } = useSyncedUser();
  const user = syncedUserData?.user;
  const userApi = useUser();
  
  // Time period filters for the leaderboard
  const TIME_FILTERS = [
    { id: 'all_time', label: t('leaderboard.timeFilters.allTime'), icon: Trophy },
    { id: 'monthly', label: t('leaderboard.timeFilters.thisMonth'), icon: Calendar },
    { id: 'weekly', label: t('leaderboard.timeFilters.thisWeek'), icon: Users }
  ];

  // This useEffect will run when users or searchQuery change
  useEffect(() => {
    if (users.length > 0) {
      // Filter the users based on search query
      const filtered = users.filter(user => 
        (user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.apartment?.toLowerCase().includes(searchQuery.toLowerCase()))
      ).slice(0, 10); // Limit to top 10 users
      
      setFilteredUsers(filtered);
      
      // Get the current user from the leaderboard
      const current = users.find(u => u.isCurrentUser || (user && u.id === user.id));
      setCurrentUser(current);
    }
  }, [users, searchQuery, user]);

  // Fetch users on component mount and when activeTime changes
  useEffect(() => {
    fetchLeaderboardData();
  }, [activeTime]);

  const fetchLeaderboardData = async () => {
    setLoading(true);
    try {
      const response = await userApi.getAllUsers();
      console.log("Users fetched successfully:", response);
      
      // Make sure response is an array
      const userData = Array.isArray(response) ? response : [];
      
      // Filter out users with status=0
      const activeUsers = userData.filter(user => user.status !== 0);
      
      // Sort users by points
      const sortedUsers = [...activeUsers].sort((a, b) => (b.totalPointsEarned || 0) - (a.totalPointsEarned || 0));
      
      // Assign ranks
      const rankedUsers = sortedUsers.map((user, index) => ({
        ...user,
        rank: index + 1,
        // Make sure name and apartment exist to avoid errors
        name: user.name || 'Anonymous',
        apartment: user.apartment || t('parking.unknown'),
        totalPointsEarned: user.totalPointsEarned || 0
      }));
      
      setUsers(rankedUsers);
      setLoading(false);
    } catch (error) {
      console.error(t('leaderboard.errors.fetchFailed'), error);
      setLoading(false);
      // Set empty arrays to prevent errors
      setUsers([]);
      setFilteredUsers([]);
    }
  };
  
  // Count active filters for badge
  const activeFilterCount = [
    activeTime !== 'all_time',
    searchQuery.trim() !== ''
  ].filter(Boolean).length;
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100dvh-4rem)]">
        <Loader />
      </div>
    );
  }
  
  return (
    <div className="space-y-4 pb-16 md:pb-6 overflow-y-auto max-h-[calc(100dvh)] px-4 py-6 md:px-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl md:text-3xl font-bold">{t('leaderboard.title')}</h1>
        <div className="flex items-center gap-2">
          {/* Mobile filter button */}
          <Button 
            variant="outline" 
            size="icon" 
            className="md:hidden relative"
            onClick={() => setShowMobileFilters(true)}
            aria-label={t('leaderboard.actions.showFilters')}
          >
            <Filter className="h-5 w-5" />
            {activeFilterCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-primary text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
          </Button>
        </div>
      </div>
      
      {/* Info Banner */}
      <Card className="p-4 md:p-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 border-blue-200 dark:border-blue-800">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="space-y-1 md:space-y-2">
            <h2 className="text-lg md:text-xl font-semibold text-blue-800 dark:text-blue-300">{t('leaderboard.banner.title')}</h2>
            <p className="text-sm text-blue-700/80 dark:text-blue-400/80">{t('leaderboard.banner.description')}</p>
          </div>
          <Link to="/rewards" >
            <Button className="bg-blue-600 hover:bg-blue-700 text-white shadow-md md:w-auto w-full">
              <Gift className="w-4 h-4 mr-2" />
              {t('leaderboard.banner.viewRewards')}
            </Button>
          </Link>
        </div>
      </Card>
      
      {/* Desktop Time Period Filters */}
      <Card className="p-4 bg-white dark:bg-neutral-900 hidden md:block">
        <div className="flex flex-col gap-4">
          <div className="flex justify-between gap-4">
            <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-thin">
              {TIME_FILTERS.map((filter) => (
                <button
                  key={filter.id}
                  onClick={() => setActiveTime(filter.id)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg whitespace-nowrap ${
                    activeTime === filter.id
                      ? 'bg-primary text-white'
                      : 'bg-neutral-100 dark:bg-neutral-800'
                  }`}
                >
                  <filter.icon className="w-4 h-4" />
                  <span>{filter.label}</span>
                </button>
              ))}
            </div>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <input
                type="text"
                placeholder={t('leaderboard.filters.searchPlaceholder')}
                className="w-full pl-10 pr-4 py-2 rounded-lg bg-neutral-100 dark:bg-neutral-800 
                         border border-transparent focus:border-primary focus:ring-2 
                         focus:ring-primary/20 transition-all duration-200"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>
      </Card>
      
      {/* Mobile Filter Drawer */}
      <FilterDrawer 
        isOpen={showMobileFilters}
        onClose={() => setShowMobileFilters(false)}
        activeTime={activeTime}
        setActiveTime={setActiveTime}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        timeFilters={TIME_FILTERS}
      />
      
      {/* Leaderboard Table */}
      <Card className="p-0 overflow-hidden bg-white dark:bg-neutral-900">
        {/* Table header */}
        <div className="px-4 py-3 bg-neutral-50 dark:bg-neutral-800/50 border-b border-neutral-200 dark:border-neutral-700">
          <div className="grid grid-cols-12 gap-3 text-xs text-neutral-500 dark:text-neutral-400 font-medium">
            <div className="col-span-2 md:col-span-1 text-center">{t('leaderboard.table.rank')}</div>
            <div className="col-span-6 md:col-span-7">{t('leaderboard.table.resident')}</div>
            <div className="col-span-4 md:col-span-4 text-center">{t('leaderboard.table.totalPoints')}</div>
          </div>
        </div>
        
        {/* Table rows */}
        <div className="divide-y divide-neutral-200 dark:divide-neutral-800">
          {filteredUsers.length > 0 ? (
            filteredUsers.map((user) => (
              <div 
                key={user.id} 
                className={`px-4 py-3 ${user.isCurrentUser ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
              >
                <div className="grid grid-cols-12 gap-3 items-center">
                  <div className="col-span-2 md:col-span-1 flex justify-center">
                    <RankBadge rank={user.rank} />
                  </div>
                  
                  <div className="col-span-6 md:col-span-7">
                    <div className="flex items-center gap-3">
                      <Avatar className={`h-10 w-10 ${user.isCurrentUser ? 'ring-2 ring-primary' : ''}`}>
                        <AvatarImage src={user.avatar} />
                        <AvatarFallback className="text-sm">
                          {user.first_name?.split(' ').map(n => n?.[0] || '').join('') || '?'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <div className="font-medium text-sm truncate">
                          {user.first_name + ' ' + user.last_name}
                          {user.isCurrentUser && <span className="ml-1 text-primary">({t('leaderboard.user.you')})</span>}
                        </div>
                        <div className="text-xs text-muted-foreground">{user.username}</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="col-span-4 md:col-span-4 flex justify-center">
                    <div className="flex items-center gap-1 text-amber-600 dark:text-amber-500">
                      <Zap className="h-4 w-4 text-amber-500 fill-amber-500" />
                      <span className="font-semibold">{user.totalPointsEarned.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="px-4 py-8 text-center text-neutral-500">
              {t('leaderboard.emptyState.noUsers')}
            </div>
          )}
        </div>
      </Card>
      
      {/* Your Current Status Card */}
      {currentUser && (
        <Card className="p-4 md:p-6 bg-gradient-to-r from-blue-500/10 to-indigo-500/10 dark:from-blue-900/30 dark:to-indigo-900/30 border-blue-200 dark:border-blue-800 mt-6">
          <div className="flex flex-col md:flex-row items-center gap-4 md:gap-6">
            <div className="flex items-center gap-3">
              <Avatar className="h-12 w-12 ring-2 ring-primary ring-offset-2 dark:ring-offset-0">
                <AvatarImage src={currentUser.avatar} />
                <AvatarFallback className="text-sm">
                  {currentUser.first_name?.split(' ').map(n => n?.[0] || '').join('') || '?'}
                </AvatarFallback>
              </Avatar>
              
              <div>
                <p className="text-sm text-blue-700/70 dark:text-blue-300/70">{t('leaderboard.user.yourStatus')}</p>
                <h3 className="font-semibold">
                  {t('leaderboard.user.rankInfo', {
                    rank: currentUser.rank,
                    points: currentUser.totalPointsEarned.toLocaleString()
                  })}
                </h3>
              </div>
            </div> 
          </div>
        </Card>
      )}
      
      {/* No Results */}
      {filteredUsers.length === 0 && (
        <div className="text-center py-12 bg-white dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-700">
          <Users className="w-10 h-10 text-neutral-300 dark:text-neutral-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium">{t('leaderboard.emptyState.title')}</h3>
          <p className="text-muted-foreground mt-2 text-sm px-4">
            {t('leaderboard.emptyState.description')}
          </p>
          {searchQuery && (
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => setSearchQuery('')}
            >
              <X className="w-4 h-4 mr-2" />
              {t('leaderboard.actions.clearSearch')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default Leaderboard;